import { useEffect, useRef, useState, useCallback } from "react";
import { useOutsideEvent } from "./useOutsideEvent.js";

export function useMenu(initialOpen = false) {
  const [open, setOpen] = useState(initialOpen);
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const close = useCallback(() => setOpen(false), []);
  const openMenu = useCallback(() => setOpen(true), []);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  // Close only when clicking outside both button + list (the whole root)
  useOutsideEvent(rootRef, open, close);

  const onButtonKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      openMenu();
      queueMicrotask(() => {
        const items = listRef.current?.querySelectorAll('[role="menuitem"]');
        items?.[0]?.focus?.();
      });
    }
  };

  const onListKeyDown = (e) => {
    const items = Array.from(
      listRef.current?.querySelectorAll('[role="menuitem"]') || []
    );
    if (!items.length) return;
    const idx = items.findIndex((el) => el === document.activeElement);
    if (e.key === "Escape") {
      e.preventDefault();
      close();
      buttonRef.current?.focus?.();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus?.();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus?.();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus?.();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus?.();
    }
  };

  useEffect(() => {
    if (!open) return;
    const onDocKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onDocKey);
    return () => document.removeEventListener("keydown", onDocKey);
  }, [open, close]);

  return {
    open,
    toggle,
    openMenu,
    close,
    rootProps: { ref: rootRef },
    buttonProps: {
      ref: buttonRef,
      "aria-haspopup": "menu",
      "aria-expanded": open ? "true" : "false",
      onKeyDown: onButtonKeyDown,
    },
    listProps: {
      ref: listRef,
      role: "menu",
      onKeyDown: onListKeyDown,
      "data-open": open ? "true" : "false",
    },
  };
}
